import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { apiFetch, apiFetchJson } from '../../apiClient';
import BookCover from './BookCover';
import './BookSearch.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

const OWNERSHIP_OPTIONS = [
  { value: 'Owned', label: '📦 Owned' },
  { value: 'Digital', label: '💻 Digital' },
  { value: 'Borrowed', label: '🤝 Borrowed' },
  { value: 'Wishlist', label: '💭 Wishlist' },
];

function DifficultyStars({ score }) {
  if (!score && score !== 0) return null;
  const rounded = Math.round(score);
  return (
    <span className="booksearch-stars" title={`Difficulty: ${score}/5`}>
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} className={i < rounded ? 'booksearch-star-filled' : 'booksearch-star-empty'}>
          ★
        </span>
      ))}
    </span>
  );
}

function CommunityRating({ rating, count }) {
  if (!rating) return null;
  return (
    <span className="booksearch-community-rating" title={`Community rating: ${rating}/5 (${count} ratings)`}>
      ⭐ {rating} <span className="booksearch-rating-count">({count})</span>
    </span>
  );
}

function BookSearch({ factFictionMode }) {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [expandedBookId, setExpandedBookId] = useState(null);
  const [actionStatus, setActionStatus] = useState({});
  const debounceRef = useRef(null);

  const searchBooks = useCallback(async (searchQuery) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setSearched(false);
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const data = await apiFetchJson(instance, `/api/books/search?q=${encodeURIComponent(searchQuery.trim())}&type=${factFictionMode}`);
      setResults(unwrap(data));
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [instance, factFictionMode]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setResults([]);
      setSearched(false);
      return;
    }
    debounceRef.current = setTimeout(() => searchBooks(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, searchBooks]);

  const addToLibrary = useCallback(async (bookId, ownership) => {
    setOpenDropdown(null);
    setActionStatus(prev => ({ ...prev, [`lib-${bookId}`]: 'loading' }));
    try {
      const res = await apiFetch(instance, '/api/userlibrary', {
        method: 'POST',
        body: JSON.stringify({ bookId, ownership }),
      });
      setActionStatus(prev => ({ ...prev, [`lib-${bookId}`]: (res.ok || res.status === 409) ? 'success' : 'error' }));
    } catch {
      setActionStatus(prev => ({ ...prev, [`lib-${bookId}`]: 'error' }));
    }
  }, [instance]);

  const setBookStatus = useCallback(async (bookId, status) => {
    const key = status === 'Finished' ? `read-${bookId}` : `wtr-${bookId}`;
    setActionStatus(prev => ({ ...prev, [key]: 'loading' }));
    try {
      const res = await apiFetch(instance, '/api/userbookstatus', {
        method: 'POST',
        body: JSON.stringify({ bookId, status }),
      });
      if (res.ok) {
        setActionStatus(prev => ({ ...prev, [key]: 'success' }));
      } else if (res.status === 409) {
        // Already exists — try updating instead
        const updateRes = await apiFetch(instance, `/api/userbookstatus/${bookId}`, {
          method: 'PUT',
          body: JSON.stringify({ status }),
        });
        setActionStatus(prev => ({ ...prev, [key]: updateRes.ok ? 'success' : 'error' }));
      } else {
        setActionStatus(prev => ({ ...prev, [key]: 'error' }));
      }
    } catch {
      setActionStatus(prev => ({ ...prev, [key]: 'error' }));
    }
  }, [instance]);

  // Empty state — no search yet
  if (!searched && !loading) {
    return (
      <div className="booksearch-container">
        <div className="booksearch-input-wrap">
          <span className="booksearch-input-icon">🔍</span>
          <input
            type="text"
            className="booksearch-input"
            placeholder={t('bookClub.bookSearch.searchPlaceholder', { mode: factFictionMode })}
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>
        <div className="booksearch-empty">
          <span className="booksearch-empty-icon">📚</span>
          <p>{t('bookClub.bookSearch.getStarted')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="booksearch-container">
      <div className="booksearch-input-wrap">
        <span className="booksearch-input-icon">🔍</span>
        <input
          type="text"
          className="booksearch-input"
          placeholder={t('bookClub.bookSearch.searchPlaceholder', { mode: factFictionMode })}
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
      </div>

      {loading && (
        <div className="booksearch-loading">
          <span className="booksearch-spinner" />
          <p>Searching…</p>
        </div>
      )}

      {!loading && searched && results.length === 0 && (
        <div className="booksearch-empty">
          <span className="booksearch-empty-icon">🔎</span>
          <p>{t('bookClub.bookSearch.noResults', { query })}</p>
        </div>
      )}

      {!loading && results.length > 0 && (
        <div className="booksearch-grid">
          {results.map(book => (
            <div key={book.id} className={`booksearch-card ${expandedBookId === book.id ? 'booksearch-card--expanded' : ''}`}>
              <div className="booksearch-card-clickable" onClick={() => setExpandedBookId(prev => prev === book.id ? null : book.id)} role="button" tabIndex={0}>
                <div className="booksearch-cover">
                  <BookCover src={book.coverImageUrl} alt={book.title} className="booksearch-cover-img" />
                </div>
                <div className="booksearch-info">
                  <h3 className="booksearch-title" title={book.title}>{book.title}</h3>
                  <p className="booksearch-author">{t('bookClub.common.by')} {book.author}</p>
                  <div className="booksearch-meta">
                    {book.pageCount > 0 && (
                      <span className="booksearch-pages">{book.pageCount} {t('bookClub.common.pages')}</span>
                    )}
                    <CommunityRating rating={book.averageRating} count={book.ratingCount} />
                    <DifficultyStars score={book.difficultyScore} />
                  </div>
                </div>
                <span className="booksearch-card-chevron">{expandedBookId === book.id ? '▲' : '▼'}</span>
              </div>
              {expandedBookId === book.id && (
                <div className="booksearch-card-detail">
                  {book.description ? (
                    <p className="booksearch-card-desc">{book.description.length > 250 ? book.description.slice(0, 250) + '…' : book.description}</p>
                  ) : (
                    <p className="booksearch-card-desc booksearch-card-desc--empty">{t('bookClub.common.noDescription')}</p>
                  )}
                </div>
              )}
              <div className="booksearch-actions">
                <div className="booksearch-dropdown-wrap">
                  <button
                    className={`booksearch-btn booksearch-btn-library ${actionStatus[`lib-${book.id}`] || ''}`}
                    onClick={() => setOpenDropdown(openDropdown === book.id ? null : book.id)}
                    disabled={actionStatus[`lib-${book.id}`] === 'loading' || actionStatus[`lib-${book.id}`] === 'success'}
                  >
                    {actionStatus[`lib-${book.id}`] === 'loading' ? '⏳' :
                     actionStatus[`lib-${book.id}`] === 'success' ? '✓ ' + t('bookClub.bookSearch.inLibrary') :
                     actionStatus[`lib-${book.id}`] === 'error' ? '⚠ ' + t('bookClub.common.retry') : '📚 ' + t('bookClub.bookSearch.addToLibrary')}
                  </button>
                  {openDropdown === book.id && (
                    <div className="booksearch-dropdown">
                      {OWNERSHIP_OPTIONS.map(opt => (
                        <button
                          key={opt.value}
                          className="booksearch-dropdown-item"
                          onClick={() => addToLibrary(book.id, opt.value)}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  className={`booksearch-btn booksearch-btn-wtr ${actionStatus[`wtr-${book.id}`] || ''}`}
                  onClick={() => setBookStatus(book.id, 'WantToRead')}
                  disabled={actionStatus[`wtr-${book.id}`] === 'loading' || actionStatus[`wtr-${book.id}`] === 'success'}
                >
                  {actionStatus[`wtr-${book.id}`] === 'loading' ? '⏳' :
                   actionStatus[`wtr-${book.id}`] === 'success' ? '✓ ' + t('bookClub.bookSearch.added') :
                   actionStatus[`wtr-${book.id}`] === 'error' ? '⚠ ' + t('bookClub.common.retry') : '📌 ' + t('bookClub.bookSearch.wantToRead')}
                </button>
                <button
                  className={`booksearch-btn booksearch-btn-read ${actionStatus[`read-${book.id}`] || ''}`}
                  onClick={() => setBookStatus(book.id, 'Finished')}
                  disabled={actionStatus[`read-${book.id}`] === 'loading' || actionStatus[`read-${book.id}`] === 'success'}
                >
                  {actionStatus[`read-${book.id}`] === 'loading' ? '⏳' :
                   actionStatus[`read-${book.id}`] === 'success' ? '✓ ' + t('bookClub.bookSearch.read') :
                   actionStatus[`read-${book.id}`] === 'error' ? '⚠ ' + t('bookClub.common.retry') : '✓ ' + t('bookClub.bookSearch.markAsRead')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default BookSearch;
